import { arrayLiteralToCamelCase } from 'pos-shared/utils/textHelpers.ts';
import { z } from 'zod';

import { DashboardOrderStatusEnum } from './dashboardSchema.ts';
import {
  JobAuthorisationEnum,
  JobStatusEnum,
  SlotTypeEnum,
} from './jobSchema.ts';
import { PurchaseOrderStatusEnum } from './purchaseOrderSchema.ts';
import { serviceTypeEnum } from './resourceManagementSchema.ts';
import {
  StockQuarantineStatusEnum,
  StockTakeStatusEnum,
  StockTransferStatusEnum,
} from './stockSchema.ts';

export const LegendStatusTranslationEnum = z.enum([
  'completed',
  'toDo',
  'inProgress',
  'awaitingCaf',
  'jobIssues',
]);

/// @deprecated: use static config / ref data store
export const OrderStatusEnum = z.enum([
  'Unknown',
  'New',
  'Initiated',
  'InProgress',
  'Approved',
  'OnHold',
  'Invoiced',
  'Cancelled',
  'Completed',
  'Ordered',
]);

// Order statuses where the order is considered closed: edit actions are blocked
// and the customer snapshot is treated as source-of-truth on the FE.
export const CLOSED_ORDER_STATUSES = ['Completed', 'Cancelled'] as const;

export const isClosedOrderStatus = (status: string | null | undefined) =>
  !!status && (CLOSED_ORDER_STATUSES as readonly string[]).includes(status);

/// @deprecated: use static config / ref data store

export const JobPartStatusEnum = z.enum(['inStock', 'outOfStock']);

export const ActiveInactiveEnum = z.enum(['Active', 'Inactive']);

export const ApprovalStatusEnum = z.enum(['Approved', 'Pending', 'Declined']);

export const OpenClosedEnum = z.enum(['Open', 'Closed']);

export const SupplierStatusEnum = z.enum(['Unknown', 'Active', 'Inactive']);

export const CustomerTypeEnum = z.enum(['Retail', 'Fleet']);

export const FleetStatusEnum = z.enum(['Active', 'Inactive', 'OnHold']);

export const JobItemPartStatusEnum = z.enum([
  'Speculative',
  'Ordered',
  'WIP',
  'In Transit',
  'Cancelled',
  'Full Return',
  'Partial Return',
  'Not Fitted',
  'Fulfilled',
  'Not Ordered',
]);

export const OrderPaymentStatusEnum = z.enum([
  'outstanding',
  'partiallyPaid',
  'paid',
  'refunded',
  'partiallyRefunded',
]);

export const OrderCreationSourceEnum = z.enum([
  'CustomerRequest',
  'OtherJobDoingIdentified',
  'SystemDataIdentified',
]);

export const ReasonOptionsSchema = z.object({
  reasons: z.record(z.string(), z.string()),
});

const OrderStatusEnumTranslation = z.enum([
  'Initiated',
  'WIP - Building',
  'WIP - Awaiting Fulfilment',
  'Completed',
  'Cancelled',
]);

const OrderedUnitStatusTranslation = z.enum([
  'Pending',
  'Ordered',
  'Failed',
  'To Return',
]);

export const StockUnitEnumTranslation = z.enum([
  'InStock',
  'InTransit',
  'Transferring',
  'Transferred',
  'ReleasedToJob',
  'Invoiced',
  'Quarantined',
  'ReturnToSupplier',
  'CollectedBySupplier',
  'RemovedByAdjustment',
  'AssignedToJob',
  'AllocatedToJob',
  'WrittenOff',
  'Scrap',
  'CollectedAsScrap',
]);

export const B2BAIStatusEnum = z.enum([
  'Authorized',
  'Awaiting FM',
  'Requires FM',
  'Not Authorized',
  'Partial Authorization',
  'Complete',
  'Completed',
  'Cancelled',
]);

const UnResolvedJobStatusEnum = z.enum(['Bypassed', 'Sub Contracted']); // Not sure if we ever even use these. Can probably be removed

const UnResolvedTranslatedStatuses = z.enum([
  'Raise Return',
  'Not Required',
  'Ready',
  'Returned',
  'Delivered',
  'Fitted',
  'Not Fitted',
  'Not Ordered',
  'On Stop',
  'Part Cancelled',
  'Paused',
  'Not Started',
  'Pending Approval',
  'Awaiting Fulfilment',
  'Scheduled',
  'Expired',
  'Imported',
]); // No idea where these come from, but don't want to remove until we know

export const StatusTypeEnum = z.enum([
  ...CustomerTypeEnum.options,
  ...FleetStatusEnum.options,
  ...serviceTypeEnum.options,
  ...JobAuthorisationEnum.options,
  ...JobPartStatusEnum.options,
  ...JobItemPartStatusEnum.options,
  ...JobStatusEnum.options,
  ...SlotTypeEnum.options,
  ...SupplierStatusEnum.options,
  ...ActiveInactiveEnum.options,
  ...ApprovalStatusEnum.options,
  ...PurchaseOrderStatusEnum.options,
  ...OrderPaymentStatusEnum.options,
  ...OrderStatusEnumTranslation.options,
  ...StockUnitEnumTranslation.options,
  ...StockQuarantineStatusEnum.options,
  ...OpenClosedEnum.options,
  ...OrderedUnitStatusTranslation.options,
  ...UnResolvedJobStatusEnum.options,
  ...UnResolvedTranslatedStatuses.options,
  ...DashboardOrderStatusEnum.options,
  ...B2BAIStatusEnum.options,
  ...StockTakeStatusEnum.options,
  ...StockTransferStatusEnum.options,
]);

export const StatusTypeCamelEnum = z.enum(
  arrayLiteralToCamelCase(StatusTypeEnum.options)
);

export type ActiveInactiveType = z.infer<typeof ActiveInactiveEnum>;
export type ApprovalStatusType = z.infer<typeof ApprovalStatusEnum>;
export type ReasonOptionsModel = z.infer<typeof ReasonOptionsSchema>;
export type OrderStatusType = z.infer<typeof OrderStatusEnum>;
export type SlotTypeType = z.infer<typeof SlotTypeEnum>;
export type SupplierStatusType = z.infer<typeof SupplierStatusEnum>;
export type CustomerType = z.infer<typeof CustomerTypeEnum>;
export type FleetStatusType = z.infer<typeof FleetStatusEnum>;
export type OrderPaymentsStatusType = z.infer<typeof OrderPaymentStatusEnum>;
export type StatusType = z.infer<typeof StatusTypeEnum>;
export type StatusTypeCamel = z.infer<typeof StatusTypeCamelEnum>;
export type JobItemPartStatusType = z.infer<typeof JobItemPartStatusEnum>;
export type OrderCreationSourceType = z.infer<typeof OrderCreationSourceEnum>;
export type B2BAIStatusEnumType = z.infer<typeof B2BAIStatusEnum>;
export type LegendStatusTranslationType = z.infer<
  typeof LegendStatusTranslationEnum
>;
